import { useMemo, useState } from 'react';
import { FiBell, FiChevronLeft, FiChevronRight, FiEdit2, FiSettings } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';

import { Header } from '@/components';
import { ButtonOutlinedSmall } from '@/components/Button';
import { Radio, Switch } from '@/components/Inputs';
import { FRONTEND_FEATURES } from '@/permissions/frontend-features';
import { useFrontendFeature } from '@/permissions/useFrontendFeature';
import {
  useNotificationDeliveryQuery,
  useUpdateNotificationDeliveryMutation,
} from '@/store/notification-delivery/notification-delivery.store';
import { NotificationDeliveryOption } from '@/store/notification-delivery/notification-delivery.types';
import {
  useBulkUpsertNotificationEventTypesMutation,
  useNotificationEventTypesQuery,
  useUpdateNotificationEventTypeMutation,
} from '@/store/notification-event-types/notification-event-types.store';
import {
  useNotificationMethodsQuery,
  useUpdateNotificationMethodMutation,
} from '@/store/notification-settings/notification-settings.store';
import { NotificationMethodType } from '@/store/notification-settings/notification-settings.types';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';

import { EditEmailModal } from './components/EditEmailModal';
import { EditPhoneModal } from './components/EditPhoneModal';
import { ManagePushDevicesModal } from './components/ManagePushDevicesModal';
import {
  type AlertCategory,
  type AlertGroupId,
  ALERT_CATEGORIES,
  alertCategoriesFor,
  CATEGORY_PARAM,
  CHANNEL_META,
  CHANNEL_ORDER,
  DELIVERY_OPTIONS,
} from './NotificationsSettingsPage.data';
import s from './NotificationsSettingsPage.module.scss';

const NotificationSettingsContent = () => {
  const { data: methods = [] } = useNotificationMethodsQuery();
  const { data: eventTypes = [] } = useNotificationEventTypesQuery();
  const { data: delivery } = useNotificationDeliveryQuery();

  const { mutate: updateMethod } = useUpdateNotificationMethodMutation();
  const { mutate: updateEventType } = useUpdateNotificationEventTypeMutation();
  const { mutate: bulkUpsertEventTypes } = useBulkUpsertNotificationEventTypesMutation();
  const { mutate: updateDelivery } = useUpdateNotificationDeliveryMutation();

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get(CATEGORY_PARAM);
  const activeCategory = ALERT_CATEGORIES.find((c) => c.id === categoryFromUrl)?.id ?? null;
  const canPay = useFrontendFeature(FRONTEND_FEATURES.PaymentsUI);
  const categories = useMemo(() => alertCategoriesFor(canPay), [canPay]);

  const setActiveCategory = (cat: AlertGroupId | null) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (cat) {
        next.set(CATEGORY_PARAM, cat);
      } else {
        next.delete(CATEGORY_PARAM);
      }
      return next;
    });

  const [openModal, setOpenModal] = useState<NotificationMethodType | null>(null);

  const methodsByType = (type: NotificationMethodType) => methods.filter((m) => m.type === type);
  const primaryMethod = (type: NotificationMethodType) => methodsByType(type)[0];
  const isChannelActive = (type: NotificationMethodType) =>
    methodsByType(type).some((m) => m.active);

  const allowAll = methods.some((m) => m.active);
  const hasMethods = methods.length > 0;
  const activeChannelCount = CHANNEL_ORDER.filter(isChannelActive).length;

  const heroHint = (() => {
    if (allowAll) return 'Turning this off pauses delivery on every channel below.';
    if (!hasMethods) return 'Please add a channel below to start receiving notifications.';
    return 'Notifications are paused. Please turn this on to manage channels and alerts.';
  })();

  const currentDelivery = delivery?.deliveryOption ?? NotificationDeliveryOption.INSTANT;

  const eventRecord = (key: string) => eventTypes.find((e) => e.eventType === key);
  const eventTypesOf = (key: string): NotificationMethodType[] => {
    const rec = eventRecord(key);
    return rec ? rec.type : [...CHANNEL_ORDER];
  };
  const isEventEnabled = (key: string) => eventTypesOf(key).length > 0;
  const enabledEventCount = (cat: AlertCategory) =>
    cat.events.filter((ev) => isEventEnabled(ev.key)).length;

  const categoryDeliveryTypes = (cat: AlertCategory): NotificationMethodType[] => {
    const active = new Set<NotificationMethodType>();
    cat.events
      .filter((ev) => isEventEnabled(ev.key))
      .forEach((ev) => eventTypesOf(ev.key).forEach((t) => active.add(t)));
    return CHANNEL_ORDER.filter((t) => active.has(t));
  };

  const channelSub = (type: NotificationMethodType) => {
    if (type === NotificationMethodType.PUSH) {
      const count = methodsByType(type).length;
      return count
        ? `${count} device${count === 1 ? '' : 's'} registered`
        : 'No devices registered';
    }
    const data = primaryMethod(type)?.data;
    if (!data) return 'Not set';
    return type === NotificationMethodType.PHONE ? formatPhoneNumber(data) : data;
  };

  const setMethodActive = (m: (typeof methods)[number], active: boolean) =>
    updateMethod({ id: m.id, type: m.type, data: m.data, active });

  const toggleAllowAll = () => {
    const target = !allowAll;
    methods.forEach((m) => {
      if (m.active !== target) setMethodActive(m, target);
    });
  };

  const toggleChannel = (type: NotificationMethodType) => {
    const list = methodsByType(type);
    if (!list.length) return;
    const target = !isChannelActive(type);
    list.forEach((m) => {
      if (m.active !== target) setMethodActive(m, target);
    });
  };

  const selectDelivery = (option: NotificationDeliveryOption) => {
    if (option !== currentDelivery) updateDelivery(option);
  };

  const toggleEvent = (cat: AlertCategory, key: string) => {
    if (isEventEnabled(key)) {
      updateEventType({ eventType: key, type: [] });
      return;
    }
    const restore = categoryDeliveryTypes(cat);
    updateEventType({ eventType: key, type: restore.length ? restore : [...CHANNEL_ORDER] });
  };

  const toggleCategoryChannel = (cat: AlertCategory, method: NotificationMethodType) => {
    const enable = !categoryDeliveryTypes(cat).includes(method);
    const items = cat.events
      .filter((ev) => isEventEnabled(ev.key))
      .map((ev) => {
        const current = eventTypesOf(ev.key);
        const type = enable
          ? CHANNEL_ORDER.filter((t) => t === method || current.includes(t))
          : current.filter((t) => t !== method);
        return { eventType: ev.key, type };
      });
    if (items.length) bulkUpsertEventTypes({ items });
  };

  const activeCategoryDef = activeCategory
    ? (categories.find((c) => c.id === activeCategory) ?? null)
    : null;

  const renderCategoryDetail = (cat: AlertCategory) => (
    <>
      <button type="button" className={s.backBtn} onClick={() => setActiveCategory(null)}>
        <FiChevronLeft /> General
      </button>

      <header className={s.detailHeader}>
        <div className={s.detailIcon}>
          <cat.icon />
        </div>
        <div>
          <h2 className={s.detailTitle}>{cat.title}</h2>
          <p className={s.detailSub}>{cat.sub}</p>
        </div>
      </header>

      <p className={s.groupLabel}>
        Notification types · {enabledEventCount(cat)}/{cat.events.length}
      </p>
      <div className={s.group}>
        {cat.events.map((ev) => (
          <div key={ev.key} className={s.row}>
            <div className={s.rowText}>
              <div className={s.rowTitle}>{ev.label}</div>
            </div>
            <Switch checked={isEventEnabled(ev.key)} onChange={() => toggleEvent(cat, ev.key)} />
          </div>
        ))}
      </div>

      <p className={s.groupLabel}>Deliver via</p>
      <div className={s.group}>
        {CHANNEL_ORDER.map((type) => {
          const meta = CHANNEL_META[type];
          const globalOff = !isChannelActive(type);
          return (
            <div key={type} className={`${s.row} ${globalOff ? s.rowDisabled : ''}`}>
              <div className={s.rowIcon}>
                <meta.icon />
              </div>
              <div className={s.rowText}>
                <div className={s.rowTitle}>{meta.label}</div>
                {globalOff && <div className={s.rowSub}>Turned off globally</div>}
              </div>
              {globalOff ? (
                <span className={s.offBadge}>Off</span>
              ) : (
                <Switch
                  checked={categoryDeliveryTypes(cat).includes(type)}
                  onChange={() => toggleCategoryChannel(cat, type)}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  const renderGeneral = () => (
    <>
      <header className={s.detailHeader}>
        <div className={s.detailIcon}>
          <FiSettings />
        </div>
        <div>
          <h2 className={s.detailTitle}>General</h2>
          <p className={s.detailSub}>Control how and where alerts reach you.</p>
        </div>
      </header>

      <div className={s.group}>
        <div className={s.hero}>
          <div className={s.detailIcon}>
            <FiBell />
          </div>
          <div className={s.heroText}>
            <div className={s.heroTitle}>Allow notifications</div>
            <div className={s.heroSub}>Receive alerts across all channels.</div>
          </div>
          <Switch checked={allowAll} onChange={toggleAllowAll} />
        </div>
      </div>
      <p className={s.heroHint}>{heroHint}</p>

      <p className={s.groupLabel}>
        Channels · {activeChannelCount}/{CHANNEL_ORDER.length}
      </p>
      <div className={s.group}>
        {CHANNEL_ORDER.map((type) => {
          const meta = CHANNEL_META[type];
          const hasMethod = !!primaryMethod(type);
          return (
            <div key={type} className={`${s.row} ${isChannelActive(type) ? '' : s.rowDisabled}`}>
              <div className={s.rowIcon}>
                <meta.icon />
              </div>
              <div className={s.rowText}>
                <div className={s.rowTitle}>{meta.label}</div>
                <div className={s.rowSub}>{channelSub(type)}</div>
              </div>
              <div className={s.rowActions}>
                <ButtonOutlinedSmall onClick={() => setOpenModal(type)}>
                  {meta.editable && <FiEdit2 />}
                  {meta.editable ? (hasMethod ? 'Edit' : 'Add') : 'Manage'}
                </ButtonOutlinedSmall>
                <Switch
                  checked={isChannelActive(type)}
                  onChange={() => toggleChannel(type)}
                  disabled={!hasMethod}
                />
              </div>
            </div>
          );
        })}
      </div>

      <p className={s.groupLabel}>Default delivery</p>
      <div className={s.group}>
        {DELIVERY_OPTIONS.map((d) => (
          /* A <label> so a click anywhere on the row reaches the hidden input —
             the Radio renders no label of its own when `label` is empty. */
          <label key={d.id} className={`${s.row} ${s.navRow}`} htmlFor={`delivery-${d.id}`}>
            <div className={s.rowText}>
              <div className={s.rowTitle}>{d.title}</div>
              <div className={s.rowSub}>{d.sub}</div>
            </div>
            <Radio
              id={`delivery-${d.id}`}
              name="default-delivery"
              checked={currentDelivery === d.id}
              onChange={() => selectDelivery(d.id)}
              label=""
            />
          </label>
        ))}
      </div>

      <p className={s.groupLabel}>Alert categories · {categories.length}</p>
      <div className={s.group}>
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`${s.row} ${s.navRow}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            <div className={s.rowIcon}>
              <cat.icon />
            </div>
            <div className={s.rowText}>
              <div className={s.rowTitle}>{cat.title}</div>
              <div className={s.rowSub}>{cat.sub}</div>
            </div>
            <span className={s.countChip}>
              {enabledEventCount(cat)}/{cat.events.length}
            </span>
            <FiChevronRight className={s.navChevron} />
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div className={s.page} key={activeCategoryDef ? activeCategoryDef.id : 'general'}>
      {activeCategoryDef ? renderCategoryDetail(activeCategoryDef) : renderGeneral()}

      {openModal === NotificationMethodType.EMAIL && (
        <EditEmailModal
          onClose={() => setOpenModal(null)}
          method={primaryMethod(NotificationMethodType.EMAIL)}
        />
      )}
      {openModal === NotificationMethodType.PHONE && (
        <EditPhoneModal
          onClose={() => setOpenModal(null)}
          method={primaryMethod(NotificationMethodType.PHONE)}
        />
      )}
      {openModal === NotificationMethodType.PUSH && (
        <ManagePushDevicesModal
          onClose={() => setOpenModal(null)}
          devices={methodsByType(NotificationMethodType.PUSH)}
        />
      )}
    </div>
  );
};

export default function NotificationsSettingsPage({
  embedded = false,
}: { embedded?: boolean } = {}) {
  return (
    <>
      {!embedded && <Header title="Notifications" backButton />}
      <NotificationSettingsContent />
    </>
  );
}
